/** Парсит строку баланса API (`1234.56`) в число рублей. */
export function parseRubAmount(value: string | number | null | undefined): number {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const normalized = value.replace(/\s/g, '').replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Полный формат: `15 678,50 ₽` */
export function formatRubFull(value: string | number | null | undefined): string {
  const rub = parseRubAmount(value);
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rub);
}

function compactUnitLabel(value: number, singular: string, few: string, many: string): string {
  const rounded = Math.abs(value);
  const int = Math.floor(rounded);
  const mod10 = int % 10;
  const mod100 = int % 100;
  if (mod10 === 1 && mod100 !== 11) return singular;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

/**
 * Сокращенный формат для сумм от 10 000 ₽ (5+ цифр в целой части).
 * 15 678 - "15 тыс. ₽", 1 500 000 - "1,5 млн ₽".
 */
export function formatRubCompact(value: string | number | null | undefined): string | null {
  const rub = parseRubAmount(value);
  const intPart = Math.floor(Math.abs(rub));
  if (intPart < 10_000) return null;

  if (intPart >= 1_000_000_000) {
    const billions = intPart / 1_000_000_000;
    const display =
      billions >= 10 ? Math.round(billions).toString() : billions.toFixed(1).replace('.0', '');
    const unit = compactUnitLabel(billions, 'млрд', 'млрд', 'млрд');
    return `${display} ${unit} ₽`;
  }

  if (intPart >= 1_000_000) {
    const millions = intPart / 1_000_000;
    const display =
      millions >= 10 ? Math.round(millions).toString() : millions.toFixed(1).replace('.0', '');
    const unit = compactUnitLabel(millions, 'млн', 'млн', 'млн');
    return `${display} ${unit} ₽`;
  }

  const thousands = Math.floor(intPart / 1000);
  const unit = compactUnitLabel(thousands, 'тыс.', 'тыс.', 'тыс.');
  return `${thousands} ${unit} ₽`;
}

export function formatRubDisplay(value: string | number | null | undefined): {
  primary: string;
  full: string;
  isCompact: boolean;
} {
  const full = formatRubFull(value);
  const compact = formatRubCompact(value);
  if (compact) {
    return { primary: compact, full, isCompact: true };
  }
  return { primary: full, full, isCompact: false };
}

/** Для сумм транзакций со знаком: `+1 000,00 ₽` */
export function formatSignedRub(amountRub: string, positive: boolean): string {
  const full = formatRubFull(Math.abs(parseRubAmount(amountRub)));
  const numeric = full.replace('₽', '').trim();
  return `${positive ? '+' : '−'}${numeric} ₽`;
}
